import type { AdminExhibition } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useExhibitionProductsQuery } from '@/entities/exhibition';
import { PublishedProductPicker } from '@/entities/product';
import { useAddExhibitionProduct } from '../model/use-add-exhibition-product';
import { useRemoveExhibitionProduct } from '../model/use-remove-exhibition-product';
import { ExhibitionLinkedProductList } from './exhibition-linked-product-list';

interface ExhibitionProductsModalProps {
  readonly exhibition: AdminExhibition;
  /** 연결이 바뀌면 부른다 — 묶인 상품 목록과 기획전 목록을 함께 무효화한다. */
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

/** 기획전 상품 관리 모달 — 위에서 공개 상품을 찾아 묶고, 아래에서 묶인 상품을 뺀다. */
export const ExhibitionProductsModal = ({
  exhibition,
  onChanged,
  onClose,
}: ExhibitionProductsModalProps): ReactElement => {
  const linkedQuery = useExhibitionProductsQuery(exhibition.id);
  const addAction = useAddExhibitionProduct({ onSuccess: onChanged });
  const removeAction = useRemoveExhibitionProduct({ onSuccess: onChanged });

  const linked = linkedQuery.data ?? [];
  const isBusy = addAction.isPending || removeAction.isPending;
  // 첫 조회가 실패한 경우만 오류로 다룬다 — 백그라운드 재조회가 실패해도 isError 는 true 로 남지만
  // 받아 둔 목록(data)은 그대로 쓸 수 있으니 목록을 그대로 보여 주고 추가도 막지 않는다.
  // 첫 조회 실패는 다른 탭에서 기획전이 지워지는 등의 경우라 서버 메시지를 보여 주고,
  // 묶을 수 없는 상태이니 추가 버튼도 잠근다 — 지운 기획전에 상품을 더 묶는 요청이 나가지 않게.
  const isFirstLoadError = linkedQuery.isError && linkedQuery.data === undefined;
  // 방금 한 추가·제외 오류가 먼저 — 조회 오류가 가리지 않게 한다.
  const errorMessage =
    addAction.error?.message ??
    removeAction.error?.message ??
    (isFirstLoadError ? (linkedQuery.error?.message ?? '기획전을 불러오지 못했습니다.') : null);

  // 추가·제외는 오류 한 줄을 함께 쓴다. 새 요청을 보내기 전에 두 요청의 지난 오류를 모두 지운다 —
  // 아니면 다른 요청이 성공해도 옛 오류가 그 요청이 다시 돌 때까지 남는다.
  const clearErrors = (): void => {
    addAction.reset();
    removeAction.reset();
  };

  const handleAdd = (productId: string): void => {
    clearErrors();
    addAction.addProduct(exhibition.id, productId);
  };

  const handleRemove = (productId: string): void => {
    clearErrors();
    removeAction.removeProduct(exhibition.id, productId);
  };

  return (
    <PublishedProductPicker
      title="기획전 상품 관리"
      description={`"${exhibition.title}" 에 묶을 상품 — 공개된 상품만 묶을 수 있습니다.`}
      linkedLabel="묶임"
      linkedProductIds={linked.map((product) => product.productId)}
      isAddDisabled={isFirstLoadError}
      isPending={isBusy}
      errorMessage={errorMessage}
      onAdd={handleAdd}
      onClose={onClose}
    >
      {isFirstLoadError ? null : (
        <ExhibitionLinkedProductList
          products={linked}
          isPending={linkedQuery.isPending}
          isBusy={isBusy}
          onRemove={handleRemove}
        />
      )}
    </PublishedProductPicker>
  );
};
